import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './SitePopup.css';
import { useLanguage } from '../LanguageContext';

const API_ORIGIN = 'https://valleyapi-dr9y.onrender.com';
const VALID_POSITIONS = new Set([
  'top-left', 'top-center', 'top-right',
  'center-left', 'center', 'center-right',
  'bottom-left', 'bottom-center', 'bottom-right',
]);

const resolveImageUrl = (path) => {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

export default function SitePopup() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const closeButtonRef = useRef(null);
  const isPrivateArea = pathname.startsWith('/admin') || pathname.startsWith('/control');

  useEffect(() => {
    if (isPrivateArea) return undefined;

    const controller = new AbortController();
    const loadPopup = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/site-popup`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data.enabled && data.image_path) {
          setPopup({ ...data, imageUrl: resolveImageUrl(data.image_path) });
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Could not load site popup:', error);
      }
    };

    loadPopup();
    return () => controller.abort();
  }, [isPrivateArea]);

  const visible = !isPrivateArea && popup && !dismissed;

  useEffect(() => {
    if (!visible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setDismissed(true);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible]);

  if (!visible) return null;

  const popupImage = <img src={popup.imageUrl} alt={t('Featured promotion', 'Promoción destacada')} />;
  const position = VALID_POSITIONS.has(popup.position) ? popup.position : 'center';

  return (
    <div
      className={`site-popup-backdrop position-${position} ${popup.blur_background ? 'has-blur' : ''}`}
      role="presentation"
    >
      <div className="site-popup-dialog" role="dialog" aria-modal="true" aria-label={t('Featured promotion', 'Promoción destacada')}>
        <button
          ref={closeButtonRef}
          type="button"
          className="site-popup-close"
          aria-label={t('Close promotion', 'Cerrar promoción')}
          onClick={() => setDismissed(true)}
        >
          <span aria-hidden="true">×</span>
        </button>
        {popup.link_url ? (
          <a className="site-popup-link" href={popup.link_url} aria-label={t('Open featured promotion', 'Abrir promoción destacada')}>
            {popupImage}
          </a>
        ) : (
          <div className="site-popup-image">{popupImage}</div>
        )}
      </div>
    </div>
  );
}
